<!-- eslint-disable vue/no-mutating-props -->
<!-- 订单中心的统计、筛选、列表与分页展示。数据请求和弹窗打开由父页面处理。 -->
<template>
  <section class="order-panel">
    <div class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div class="filter-bar">
        <el-input v-model.trim="filters.orderNo" clearable placeholder="订单号" class="filter-control" @keyup.enter="emit('search')" />
        <el-input v-model.trim="filters.phone" clearable placeholder="手机号" class="filter-control" @keyup.enter="emit('search')" />
        <el-select v-if="showStoreFilter" v-model="filters.storeId" clearable filterable placeholder="全部授权门店" class="filter-control">
          <el-option v-for="store in storeOptions" :key="store.id" :label="store.name" :value="store.id" />
        </el-select>
        <el-select v-model="filters.status" clearable placeholder="订单状态" class="filter-control">
          <el-option v-for="option in orderStatusOptions" :key="option.value" :label="option.label" :value="option.value" />
        </el-select>
        <el-select v-model="filters.deliveryType" clearable placeholder="配送方式" class="filter-control">
          <el-option v-for="option in deliveryTypeOptions" :key="option.value" :label="option.label" :value="option.value" />
        </el-select>
        <div class="filter-bar__actions">
          <button class="search-button" @click="emit('search')">搜索</button>
          <button class="reset-button" @click="emit('reset')">重置</button>
        </div>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>订单号</th>
              <th>用户</th>
              <th>门店</th>
              <th>订单状态</th>
              <th>支付状态</th>
              <th>配送方式</th>
              <th class="is-right">实付金额</th>
              <th>下单时间</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in orders" :key="row.id" v-memo="[row.id, row.status, row.paymentStatus, row.orderNo]">
              <td><span class="cell-text">{{ row.orderNo }}</span></td>
              <td>
                <div class="cell-stack">
                  <span class="cell-text">{{ row.userNickname || '匿名用户' }}</span>
                  <small>{{ row.userPhone || '--' }}</small>
                </div>
              </td>
              <td><span class="cell-text">{{ row.storeName || row.storeId }}</span></td>
              <td><span :class="['status-tag', toneClass(orderStatusTone(row.status))]">{{ getOrderStatusText(row.status) }}</span></td>
              <td><span :class="['status-tag', toneClass(paymentStatusTone(row.paymentStatus))]">{{ getPaymentStatusText(row.paymentStatus) }}</span></td>
              <td><span class="cell-text">{{ getDeliveryTypeText(row.deliveryType) }}</span></td>
              <td class="is-right">{{ formatAmount(row.paidAmount ?? row.payableAmount) }}</td>
              <td>{{ row.createdAt }}</td>
              <td class="is-right">
                <div class="row-actions">
                  <button v-if="getQuickActionLabel(row)" class="row-actions__text" :title="getQuickActionLabel(row)" @click="handleQuickStatus(row)">
                    {{ getQuickActionLabel(row) }}
                  </button>
                  <button
                    v-if="canCancelDeliveryFee(row)"
                    class="row-actions__text row-actions__text--danger"
                    title="取消配送费"
                    @click="emit('cancelDeliveryFee', row.id)"
                  >
                    取消配送费
                  </button>
                  <button title="查看详情" @click="emit('openOrder', row.id)"><el-icon><View /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!orders.length && !loading"><td colspan="9"><el-empty description="暂无订单数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="page" class="table-footer">
        <span>共 {{ page.total }} 条记录，第 {{ page.page }}/{{ totalPages }} 页</span>
        <div>
          <button :disabled="page.page <= 1" @click="emit('changePage', page.page - 1)">上一页</button>
          <button class="is-active">{{ page.page }}</button>
          <button :disabled="!page.hasMore" @click="emit('changePage', page.page + 1)">下一页</button>
        </div>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { toRefs } from 'vue'
import { View } from '@element-plus/icons-vue'
import type { PageResult } from '@jiaofa/api-types'
import { DeliveryType, OrderStatus, type PaymentStatus } from '@jiaofa/constants'
import type { AdminOrder } from '@/api/order'
import type { AdminStore } from '@/api/store'
import type { OperationStat } from '@/data/operationDesign'

type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

interface OrderFilters {
  orderNo: string
  phone: string
  storeId: string
  status: OrderStatus | ''
  deliveryType: DeliveryType | ''
  page: number
  pageSize: number
}

interface OrderOption<T> {
  value: T
  label: string
}

const props = defineProps<{
  stats: OperationStat[]
  orders: AdminOrder[]
  page?: PageResult<AdminOrder>
  filters: OrderFilters
  storeOptions: AdminStore[]
  showStoreFilter: boolean
  orderStatusOptions: OrderOption<OrderStatus>[]
  deliveryTypeOptions: OrderOption<DeliveryType>[]
  loading: boolean
  totalPages: number
  formatAmount: (amountInFen: number) => string
  getOrderStatusText: (status: OrderStatus) => string
  getPaymentStatusText: (status: PaymentStatus) => string
  getDeliveryTypeText: (type: DeliveryType) => string
  orderStatusTone: (status: OrderStatus) => StatusTone
  paymentStatusTone: (status: PaymentStatus) => StatusTone
  toneClass: (tone: StatusTone) => string
  /** 该订单当前是否可取消配送费（自取 / 配送费为 0 / 终态时为 false）。 */
  canCancelDeliveryFee: (order: AdminOrder) => boolean
}>()

const emit = defineEmits<{
  search: []
  reset: []
  openOrder: [id: string]
  updateStatus: [id: string, status: OrderStatus]
  cancelDeliveryFee: [id: string]
  changePage: [page: number]
}>()

const {
  stats,
  orders,
  page,
  filters,
  storeOptions,
  showStoreFilter,
  orderStatusOptions,
  deliveryTypeOptions,
  loading,
  totalPages,
  formatAmount,
  getOrderStatusText,
  getPaymentStatusText,
  getDeliveryTypeText,
  orderStatusTone,
  paymentStatusTone,
  toneClass,
} = toRefs(props)
function getQuickActionLabel(row: AdminOrder): string {
  if (row.status === OrderStatus.PendingAccept) return '接单'
  if (row.status === OrderStatus.Producing) return '出餐'
  if (row.status === OrderStatus.PendingPickup) return '完成'
  if (row.status === OrderStatus.Delivering) return '完成'
  return ''
}

function getQuickActionStatus(row: AdminOrder): OrderStatus | undefined {
  if (row.status === OrderStatus.PendingAccept) return OrderStatus.Producing
  if (row.status === OrderStatus.Producing) return OrderStatus.PendingPickup
  if (row.status === OrderStatus.PendingPickup) return OrderStatus.Completed
  if (row.status === OrderStatus.Delivering) return OrderStatus.Completed
  return undefined
}

function handleQuickStatus(row: AdminOrder): void {
  const status = getQuickActionStatus(row)
  if (status) emit('updateStatus', row.id, status)
}
</script>

<style scoped lang="scss">
.order-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.filter-control { width: 220px; }
.filter-row { display: flex; gap: 12px; }
.filter-row .filter-control { min-width: 124px; }
.filter-bar__actions { display: flex; gap: 8px; margin-left: auto; }
button { border: 0; cursor: pointer; font: inherit; }
.search-button, .reset-button { height: 36px; padding: 0 16px; border-radius: 8px; font-size: 14px; font-weight: 700; }
.search-button { background: var(--jf-color-primary); color: #fff; }
.reset-button { border: 1px solid var(--jf-color-border); background: #fff; color: var(--jf-color-text-primary); }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.is-right { text-align: right; }
.cell-text { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.cell-stack { display: flex; flex-direction: column; gap: 4px; }
.cell-stack small { color: var(--jf-color-text-secondary); font-size: 12px; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.row-actions { display: inline-flex; align-items: center; justify-content: flex-end; gap: 6px; color: var(--jf-color-primary-dark); }
.row-actions button { width: 28px; height: 28px; border-radius: 6px; display: grid; place-items: center; background: transparent; color: inherit; }
.row-actions .row-actions__text { width: auto; min-width: 44px; padding: 0 10px; border: 1px solid rgba(118, 183, 232, .45); background: rgba(118, 183, 232, .12); font-size: 12px; font-weight: 700; }
.row-actions .row-actions__text--danger { border-color: rgba(231, 101, 101, .45); background: rgba(231, 101, 101, .1); color: var(--jf-color-error); }
.row-actions button:hover { background: rgba(179, 218, 247, .3); }
.row-actions button:disabled, .table-footer button:disabled { cursor: not-allowed; opacity: .5; }
.table-footer { padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 16px; color: var(--jf-color-text-secondary); font-size: 14px; }
.table-footer div { display: flex; gap: 6px; }
.table-footer button { min-width: 32px; height: 32px; padding: 0 10px; border: 1px solid var(--jf-color-border); border-radius: 8px; background: #fff; color: var(--jf-color-text-secondary); }
.table-footer .is-active { border-color: var(--jf-color-primary); background: var(--jf-color-primary); color: #fff; }
@media (max-width: 1180px) {
  .summary-grid { grid-template-columns: 1fr; }
}
</style>
